import { useMemo, useState, memo, useCallback } from "react";
import { Link } from "react-router-dom";
import { routes } from "@/common/utils/routes";
import { IGif, IUser } from "@/common/services/types";
import { metricsBus } from "@/common/buses";
import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";
import DefaultUserIcon from "@/common/images/icons/profileIcon.svg?react";
import { className } from "@/common/utils";
import { VerifiedIcon } from "@/components/UI/VerifiedIcon";
import { useSfw } from "@/common/hooks/useSfw";
import { Button } from "@/components/buttons/Button";
import "./UserInfo.scss";

interface IUserInfoProps {
  gif: IGif;
  user: IUser;
  rel?: string;
  isFollowing: boolean;
  currentUser: CurrentUserObject | null;
  isLite: boolean;
  onProfileMetric: (value: string) => void;
  onFollow: () => void;
}

export const UserInfo: React.FC<IUserInfoProps> = memo((props) => {
  const {
    gif,
    currentUser,
    isFollowing,
    isLite,
    onFollow,
    onProfileMetric
  } = props;

  const { sanitizeUser, isLoading } = useSfw();
  const user = sanitizeUser(props.user);

  const [showMore, setShowMore] = useState(false);
  const isCurrentUserGif = currentUser?.username === user.username;
  const tagsText = useMemo(() => gif.tags.map(tag => `#${tag}`).join(" "), [gif.tags]);
  const gifDescription = useMemo(() => gif.description || user.description, [user, gif]);
  const hasCta = useMemo(() => gif.cta && gif.cta.text && gif.cta.link, [gif.cta]);
  const hasDescriptionContent = !!gifDescription || !!tagsText;
  const showFollowButton = !isCurrentUserGif && !isLite && !hasCta;
  const showDescription = !isLite && hasDescriptionContent && !hasCta;

  const handleUserClick = useCallback(() => {
    onProfileMetric(user.username);
    metricsBus.emit("watchPageNavigation", { elementType: "Creator", targetURL: routes.user(user.username) });
  }, [onProfileMetric]);

  const avatar = useMemo(() => {
    if (isLite || !user.profileImageUrl) {
      return <div className="avatarDefault"><DefaultUserIcon /></div>;
    }
    return (
      <img
        loading="lazy"
        src={user.profileImageUrl}
        alt={`Avatar of ${user.username}`}
      />
    );
  }, [isLite, user.profileImageUrl, user.username]);

  if (isLoading) {
    return null;
  }

  const AvatarContent = (
    <>
      {avatar}
      <span className="userName">{user.name || user.username}</span>
      {user.verified && (
        <VerifiedIcon
          isStudio={user.studio}
          title={`${user.username} is a verified RedGIFs ${user.studio ? "Channel" : "creator"}`}
        />
      )}
    </>
  );

  return (
    <div className={`userInfo ${isLite ? "isLite" : ""}`}>
      <div className="userInfoWrap">
        {hasCta ? (
          <div className="userAvatar">
            {AvatarContent}
          </div>
        ) : (
          <Link
            className="userAvatar"
            to={routes.user(user.username)}
            onClick={handleUserClick}
            aria-label={`Link to ${user.username} profile`}
          >
            {AvatarContent}
          </Link>
        )}
        {showFollowButton && (
          <Button className="follow" size="xs" variant="secondary" onClick={onFollow}>
            {isFollowing ? "Following" : "Follow"}
          </Button>
        )}
      </div>
      {showDescription && (
        <p className="description" onClick={() => setShowMore(prev => !prev)}>
          <span className={className({ descriptionText: true, fullText: showMore })}>
            {gifDescription}
            {" "}
            {showMore && gifDescription && <br />}
            {tagsText}
          </span>
        </p>
      )}
    </div>
  );
});
